import {
  Flex,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
  ChartConfig,
} from "@suns/design-system";
import { useState } from "react";
import { formatPlayerChartData } from "@/shared/utils/helper-functions";
import { MetricButton } from "./MetricButton";
import { theme } from "@/shared/theme";
import { PlayerMetadataRow } from "@suns/api/generated-client/apollo";
import { MultiLineChart } from "@/components";

const chartConfig = {
  hybrid: {
    label: "Hybrid",
    color: theme.colors.purple.DEFAULT,
  },
  analytics: {
    label: "Analytics",
    color: theme.colors.magenta.DEFAULT,
  },
  scout: {
    label: "Scout",
    color: theme.colors.teal.DEFAULT,
  },
} satisfies ChartConfig;

interface CustomTickProps {
  height: number;
  x: number;
  y: number;
  payload: { value: number };
  visibleTicksCount: number;
}

const gradeLabels: Record<number, string> = {
  1: "ML",
  2: "RS",
  3: "RT",
  4: "TR",
  5: "ST",
  6: "AS",
  7: "AS+",
  8: "FR",
};

interface CustomTickProps {
  height: number;
  x: number;
  y: number;
  payload: { value: number };
  visibleTicksCount: number;
}

function CustomTick(props: CustomTickProps) {
  const { x, y, payload, visibleTicksCount, height } = props;

  const tickHeight = height / visibleTicksCount;
  const label = gradeLabels[payload.value];

  return (
    <g transform={`translate(${x},${y - tickHeight / 2})`}>
      <text x={0} y={0} textAnchor="end" fill="#666">
        {label}
      </text>
    </g>
  );
}

export function PlayerApolloGradeValueChart({
  playerMetadata,
}: {
  playerMetadata: PlayerMetadataRow[];
}) {
  const [activeChart, setActiveChart] = useState<
    keyof typeof chartConfig | null
  >(null);

  const chartDataMonthly = formatPlayerChartData("monthly", playerMetadata);
  const chartDataAnnual = formatPlayerChartData("annual", playerMetadata);

  const onMetricClick = (metric: keyof typeof chartConfig) => {
    if (activeChart === metric) {
      setActiveChart(null);
    } else {
      setActiveChart(metric);
    }
  };

  type MetricKey = keyof typeof chartConfig;

  const getMetricValues = (
    data: typeof chartDataAnnual,
    metric: MetricKey
  ) => ({
    currentValue: data[data.length - 1]?.[metric]?.toString() ?? null,
    previousValue:
      data.find((d) => d[metric] !== null)?.[metric]?.toString() ?? null,
  });

  return (
    <Flex direction="down" gap="md" className="w-full">
      <Tabs defaultValue="annual">
        <Flex direction="right" justify="between">
          <Text heading muted size="lg">
            Apollo Grade Value
          </Text>
          <TabsList>
            <TabsTrigger value="annual">Annual</TabsTrigger>
            <TabsTrigger value="monthly">Monthly</TabsTrigger>
          </TabsList>
        </Flex>
        <TabsContent value="annual">
          <Flex direction="down" gap="sm" className="w-full">
            <Flex direction="right" gap="sm" wrap>
              {(
                Object.entries(chartConfig) as [
                  MetricKey,
                  (typeof chartConfig)[MetricKey],
                ][]
              ).map(([key, config]) => {
                const values = getMetricValues(chartDataAnnual, key);
                return (
                  <MetricButton
                    key={key}
                    label={config.label}
                    color={config.color}
                    currentValue={values.currentValue}
                    active={activeChart === key}
                    onClick={() => onMetricClick(key)}
                  />
                );
              })}
            </Flex>
            <MultiLineChart
              dateRange="annual"
              chartData={chartDataAnnual.map((data) => ({
                ...data,
                snapshotDate: data.snapshotDate,
              }))}
              chartConfig={chartConfig}
              activeChart={activeChart}
              yAxisConfig={{
                minValue: 1,
                maxValue: 9,
                customTick: (
                  <CustomTick
                    height={100}
                    x={0}
                    y={0}
                    payload={{ value: 1 }}
                    visibleTicksCount={10}
                  />
                ),
              }}
              useApolloGradeColors
            />
          </Flex>
        </TabsContent>
        <TabsContent value="monthly">
          <Flex direction="down" gap="sm" className="w-full">
            <Flex direction="right" gap="sm" wrap>
              {(
                Object.entries(chartConfig) as [
                  MetricKey,
                  (typeof chartConfig)[MetricKey],
                ][]
              ).map(([key, config]) => {
                const values = getMetricValues(chartDataMonthly, key);
                return (
                  <MetricButton
                    key={key}
                    label={config.label}
                    color={config.color}
                    currentValue={values.currentValue}
                    active={activeChart === key}
                    onClick={() => onMetricClick(key)}
                  />
                );
              })}
            </Flex>
            <MultiLineChart
              dateRange="monthly"
              chartData={chartDataMonthly.map((data) => ({
                ...data,
                snapshotDate: data.snapshotDate,
              }))}
              chartConfig={chartConfig}
              activeChart={activeChart}
              yAxisConfig={{
                minValue: 1,
                maxValue: 9,
                customTick: (
                  <CustomTick
                    height={100}
                    x={0}
                    y={0}
                    payload={{ value: 1 }}
                    visibleTicksCount={10}
                  />
                ),
              }}
              useApolloGradeColors
            />
          </Flex>
        </TabsContent>
      </Tabs>
    </Flex>
  );
}
